/**
 * The thread. INBOX scope items 9-12, the rendering half.
 *
 * ⛔ THE THREE EMPTY STATES, EACH DECIDED RATHER THAN DEFAULTED, and the same
 * three the conversation list has - because the failure is the same one: a
 * loading thread and an empty thread and a broken thread must not look alike.
 * An agent who cannot tell them apart answers the customer differently in each
 * case and is wrong in two of them.
 */
import { useEffect } from 'react'
import { ThreadMessage } from './ThreadMessage.js'
import { startsRun } from './thread-view.js'
import type { PinnedRow, ThreadMessage as ThreadRow } from '../../../src/services/thread.js'

/**
 * ┌─ ⛔ HOW MANY MAY BE PINNED, AND THE REASON IS THE FEATURE'S OWN PURPOSE ────┐
 * │ Neither the plan nor the build specified a limit. **An unbounded pinned     │
 * │ region becomes a second thread**, which is exactly the failure pinning      │
 * │ exists to prevent: if finding the agreed price means scrolling the pinned   │
 * │ list, the pinned list has reproduced the problem it was built to solve.     │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ FIVE, AND THE NUMBER COMES FROM THE PLAN'S OWN EXAMPLES rather than from
 * taste. `Build_Order:239` names what pinning is for: *"agreed prices,
 * certification photos and medical-form warnings"* - three kinds of thing, of
 * which a booking might plausibly have one or two each. Five holds every
 * example the requirement gives with room for a second price, and it fits above
 * a thread without becoming a pane.
 *
 * ⚠️ AND IT IS ENFORCED AT THE TOP OF THE REGION RATHER THAN AT THE PIN, which
 * is the softer of the two and deliberately so. Refusing a sixth pin means
 * telling somebody to go and unpin something before they can record a medical
 * warning; showing the five most recent means the newest warning is always
 * visible and the oldest falls off. **The region says the cap is in force**, so
 * a pin that does not appear is explained rather than mysterious.
 */
const PINNED_LIMIT = 5

export type ThreadViewProps = {
  /** `null` means not loaded yet, which is NOT the same as an empty array. */
  rows: ThreadRow[] | null
  error?: string | null
  /** The message a search result arrived at: scrolled to, and marked. */
  highlightMessageId?: string | null
  /** Whether older messages remain. `useThread` owns the distinction. */
  more?: 'available' | 'loading' | 'end' | 'capped'
  onLoadMore?: () => void
  /** The signed-in user, so a note offers Edit only to its author. */
  currentUserId?: string | null
  onEditNote?: (noteId: string, body: string) => void | Promise<void>
  onDeleteNote?: (noteId: string) => void | Promise<void>
  onPinNote?: (noteId: string, pinned: boolean) => void | Promise<void>
  /** ⛔ From the server, not filtered from `rows`. See the region below. */
  pinned?: PinnedRow[]
}

export function ThreadView({
  rows,
  pinned = [],
  error = null,
  highlightMessageId = null,
  more = 'end',
  onLoadMore,
  currentUserId = null,
  onEditNote,
  onDeleteNote,
  onPinNote,
}: ThreadViewProps): React.JSX.Element {
  /**
   * ┌─ ⛔ SCROLL TO THE ARRIVED-AT MESSAGE. `Build_Order:490`. ──────────────────┐
   * │ `scrollIntoView` runs after the rows are in the document, which is why it  │
   * │ keys on `rows` as well as the id: the effect fires on mount with an empty  │
   * │ page, and again when the page arrives. Without `rows` in the deps the      │
   * │ first run finds no element and there is never a second.                    │
   * │                                                                            │
   * │ ⚠️ `block: 'center'` RATHER THAN THE DEFAULT `'start'`, because a message  │
   * │ pinned to the top edge shows none of what came before it - and the reason  │
   * │ somebody followed a search result is to read around the hit.               │
   * │                                                                            │
   * │ ⛔ WHETHER THIS ACTUALLY SCROLLS IS NOT ASSERTABLE IN jsdom, which          │
   * │ implements `scrollIntoView` as a no-op and computes no layout. It is       │
   * │ asserted in `tests/browser/search.spec.ts` against a real viewport.         │
   * └────────────────────────────────────────────────────────────────────────────┘
   */
  useEffect(() => {
    if (highlightMessageId === null) return
    const el = document.querySelector(`[data-message-id="${highlightMessageId}"]`)
    el?.scrollIntoView({ block: 'center' })
  }, [highlightMessageId, rows])

  if (error !== null) {
    /**
     * ┌─ ⛔ THE HTTP CODE IS NOT ON THIS LINE, AND THAT IS A CORRECTION ──────────┐
     * │ It read "Could not load this conversation. HTTP 502" until 2026-08-11.   │
     * │ **`HTTP 502` tells an agent nothing they can act on.**                   │
     * │                                                                        │
     * │ ⚠️ THE BUILD PLAN'S "EVERY ERROR CARRIES THE META ERROR CODE AND ITS     │
     * │ MEANING" IS ABOUT **MESSAGE** ERRORS, where the code names something an  │
     * │ agent or an admin can address - a closed window, a channel needing       │
     * │ reauthorisation, a rejected template. A TRANSPORT FAILURE IS NOT THAT.   │
     * │ Applying the rule here was reading its words rather than its subject.    │
     * │                                                                        │
     * │ ✅ THE CODE STILL REACHES A DEVELOPER. `data/inbox-api.ts` logs it at the │
     * │ point of failure, with the URL, which is more than this line carried -   │
     * │ "502" without a path is barely diagnostic either.                        │
     * └────────────────────────────────────────────────────────────────────────┘
     */
    return (
      <div className="thread__state" role="alert" data-testid="thread-error">
        Could not load this conversation.
      </div>
    )
  }

  if (rows === null) {
    return <div className="thread__state" data-testid="thread-loading" />
  }

  if (rows.length === 0) {
    /**
     * ┌─ ⛔ THIS ALSO RENDERS FOR A CONVERSATION THE SCOPE DOES NOT ADMIT, AND ──┐
     * │ CHANGING THAT IS A WORDING DECISION MADE HERE - NEVER A SECOND ANSWER    │
     * │ FROM THE ROUTE.                                                          │
     * │                                                                          │
     * │ `GET /api/inbox/conversations/:id/messages` returns the SAME empty page   │
     * │ for absent, denied by scope, malformed, and genuinely-no-messages. That   │
     * │ is deliberate: the id is in the URL, so distinguishing them would let     │
     * │ any signed-in user type ids and learn which conversations exist.          │
     * │                                                                          │
     * │ ⚠️ SO THE HONEST COMPLAINT IS THAT A REASSIGNED CONVERSATION READS AS     │
     * │ "No messages yet" RATHER THAN "you no longer have access" - and that is  │
     * │ a wording problem, not a routing one. **The fix lives on this line.**     │
     * │                                                                          │
     * │ ⛔ AND THE TEMPTING FIX IS THE ORACLE. "Ask the server whether it exists  │
     * │ before choosing the words" is a request whose answer IS the distinction   │
     * │ the uniform response withholds. Any wording chosen here must be           │
     * │ derivable from the empty page alone.                                     │
     * └──────────────────────────────────────────────────────────────────────────┘
     */
    return (
      <div className="thread__state" data-testid="thread-empty">
        No messages yet
      </div>
    )
  }

  /**
   * ┌─ ⛔ THE PINNED REGION. Build_Order:239, built 2026-09-12. ─────────────────┐
   * │ *"Pin messages AND notes to the top of a thread. Useful for agreed prices, │
   * │ certification photos and medical-form warnings."*                          │
   * │                                                                           │
   * │ ⛔ WHAT EXISTED WAS "MARK AS IMPORTANT" WEARING THE WORD PIN: a badge in   │
   * │ time order, which does not put anything at the top. The stated purpose is  │
   * │ finding an agreed price WITHOUT SCROLLING, and a badge in situ does not do │
   * │ that at all.                                                              │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️ THE PINNED ROW STAYS IN THE FEED AS WELL AS APPEARING ABOVE IT, which is
   * deliberate and is the opposite of moving it. A pinned message removed from
   * its place leaves a hole in the conversation, and the conversation is the
   * record of what was said in what order. The region is a SHORTCUT to a row,
   * not a second home for it.
   *
   * ⛔ AND IT COMES FROM THE SERVER RATHER THAN FROM `rows`. Filtering the loaded
   * range is the obvious implementation and it fails exactly when pinning
   * matters: the page is fifty rows, and the price agreed two hundred messages
   * ago is not among them. **A region that only shows pins from the part you
   * have already scrolled to shows nothing worth having.**
   */

  return (
    <ol className="thread" data-testid="thread">
      {pinned.length > 0 && (
        <li className="thread__pinned" data-testid="thread-pinned-region">
          <p className="thread__pinned-heading">
            Pinned{pinned.length >= PINNED_LIMIT && ` (${PINNED_LIMIT} maximum)`}
          </p>
          <ul className="thread__pinned-list">
            {pinned.map((row) => (
              <li key={`pin-${row.id}`} className="thread__pinned-item">
                <button
                  type="button"
                  className="thread__pinned-jump"
                  data-testid="thread-pinned-item"
                  onClick={() => {
                    document
                      .querySelector(`[data-message-id="${row.id}"]`)
                      ?.scrollIntoView({ block: 'center' })
                  }}
                >
                  {/* ⚠️ The kind is said, because a pinned note and a pinned
                      message are different things and the region flattens them
                      into one list. */}
                  <span className="thread__pinned-kind">
                    {row.type === "note" ? "Note" : "Message"}
                  </span>{' '}
                  {row.body ?? '(no text)'}
                </button>
              </li>
            ))}
          </ul>
        </li>
      )}
      {rows.map((row, i) => (
        <ThreadMessage
          key={row.id}
          message={row}
          /**
           * ⛔ `rows[i + 1]`, AND THAT IS NOT A TYPO.
           *
           * The page arrives NEWEST FIRST - `ORDER BY created_at DESC, seq DESC`
           * in `services/thread.ts`, matching the conversation list and the
           * keyset cursor. The bubble rendered ABOVE this one is therefore the
           * NEXT element in the array, so that is the neighbour a run boundary
           * is computed against.
           *
           * ⚠️ Written `rows[i - 1]` this compiles, renders, and puts the avatar
           * on the LAST bubble of each run instead of the first - which looks
           * like a styling problem rather than an ordering one.
           */
          previous={rows[i + 1]}
          highlighted={row.id === highlightMessageId}
          startsRun={startsRun(row, rows[i + 1])}
          currentUserId={currentUserId}
          onEditNote={onEditNote}
          onDeleteNote={onDeleteNote}
          onPinNote={onPinNote}
        />
      ))}

      {/*
        ⛔ LAST IN DOM ORDER, WHICH IS THE VISUAL TOP. `.thread` is
        `column-reverse` (see `thread.css`), so the oldest end of the
        conversation is the END of this list. Placing this first - the intuitive
        position for "load older" - would put it under the newest message, which
        is where the composer is.
      */}
      {more !== 'end' && (
        <li className="thread__more">
          {more === 'capped' ? (
            // Not "this is the start of the conversation". We stopped asking.
            <p className="thread__more-note" data-testid="thread-capped">
              Showing the most recent {rows.length} messages.
            </p>
          ) : (
            <button
              type="button"
              className="thread__more-btn"
              data-testid="thread-more"
              disabled={more === 'loading'}
              onClick={onLoadMore}
            >
              {more === 'loading' ? 'Loading' : 'Load Older Messages'}
            </button>
          )}
        </li>
      )}
    </ol>
  )
}
